import React, { useState } from "react";
import { HiEllipsisVertical, HiPlus } from "react-icons/hi2";
import { LuContact2 } from "react-icons/lu";
import { Link, useParams } from "react-router-dom";

import { AvatarPlaceholder } from "../../../../../../../../components/avatar/AvatarPlaceholder";
import { SettingsDropdown } from "../../../../../../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../../../components/newUI/button";
import {
  getInitialsFromFullName,
  inactiveStatusId,
} from "../../../../../../../../constants/constants";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetContactQuery } from "../../../../../../../../slices/ContactsSlice";
import {
  useDeleteContactLinkedProfileMutation,
  useGetContactLinkedProfilesQuery,
} from "../../../../../../../../slices/LinkedProfilesSlice";
import { ILinkedProfile } from "../../../../../../../../types/types";
import {
  ProfileWidgetActionButton,
  ProfileWidgetCard,
  ProfileWidgetEmptyState,
} from "../../../../../../components/profile";
import ErrorPopup from "../../../../../../modals/ErrorPopup";
import { LinkedProfileModal } from "../../../../../../modals/LinkdeProfileModal";
import { navigateToContact } from "../../../../../../utils/navigateToContacts";

export const ContactLinkedProfilesWidget = () => {
  const { customerId } = useParams();

  const { data: contact } = useGetContactQuery({
    customerId: customerId || "",
    orgId: getLinkedFirm()?.orgId || "",
  });

  const { data: contactLinkedProfiles, isFetching } =
    useGetContactLinkedProfilesQuery({
      orgId: getLinkedFirm()?.orgId,
      contactId: contact?.contactId || "",
    });
  const [deleteLinkedProfile, { isLoading: isLoadingDelete }] =
    useDeleteContactLinkedProfileMutation();

  const [currentLinkedProfile, setCurrentLinkedProfile] =
    useState<ILinkedProfile | null>(null);
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);

  const isActive = contact?.contactStatusTypeEnumId !== inactiveStatusId;

  return (
    <>
      <ProfileWidgetCard
        title={"Linked profiles"}
        action={
          <ProfileWidgetActionButton
            icon={<HiPlus size={16} />}
            title={"Add linked profile"}
            onClick={() => {
              if (!isActive) {
                setIsOpenError(true);
              } else {
                setIsOpenAdd(true);
              }

              setCurrentLinkedProfile(null);
            }}
          />
        }>
        <div>
          {isFetching ? (
            <div className={"p-4"}>
              <LottieLoading />
            </div>
          ) : !contactLinkedProfiles?.length ? (
            <ProfileWidgetEmptyState
              icon={<LuContact2 size={28} strokeWidth={1.7} />}
              title={"No linked profiles"}
              description={
                "Linked contacts and organizations will appear here."
              }
            />
          ) : (
            <div className={"max-h-[300px] overflow-y-auto pt-3"}>
              {contactLinkedProfiles?.map((item) => (
                <div
                  className={
                    "grid grid-cols-[minmax(0,1fr)_max-content] items-center justify-between gap-2 border-t border-[#F2F3F6] px-5 py-3"
                  }
                  key={item.id}>
                  <div
                    className={
                      "grid grid-cols-[max-content_minmax(0,1fr)] items-center gap-2.5 text-sm"
                    }>
                    <AvatarPlaceholder
                      type={
                        item?.linkedCustomer?.businessContactId
                          ? "purple"
                          : "blue"
                      }
                      size={"extra-small"}
                      label={getInitialsFromFullName(
                        item?.linkedCustomer?.name || "",
                      )}
                    />
                    <div className={"min-w-0 truncate"}>
                      <Link
                        to={navigateToContact({
                          type: item?.linkedCustomer?.contactId
                            ? "contact"
                            : "organization",
                          customerId: item?.linkedCustomerId || "",
                          id: item?.linkedCustomer?.businessContactId || "",
                        })}
                        className={
                          "block truncate text-[13.5px] font-bold text-[#1F2433] hover:text-[#6B4EFF]"
                        }>
                        {item?.linkedCustomer?.name}
                      </Link>
                      <div className={"truncate text-[12.5px] text-[#9097A8]"}>
                        {item?.relation}
                      </div>
                    </div>
                  </div>
                  <div onClick={(event) => event.stopPropagation()}>
                    <SettingsDropdown
                      actionsList={[
                        {
                          name: "Edit",
                          action: () => {
                            setCurrentLinkedProfile(item);
                            setIsOpenAdd(true);
                          },
                        },
                        {
                          name: "Delete",
                          disabled: isLoadingDelete,
                          action: () => {
                            if (!isLoadingDelete) {
                              deleteLinkedProfile(item);
                            }
                          },
                        },
                      ]}>
                      <Button
                        type={"button"}
                        variant={"outline"}
                        size={"sm"}
                        iconOnly
                        autoWidth
                        leftIcon={<HiEllipsisVertical size={18} />}
                        className={"shrink-0"}
                      />
                    </SettingsDropdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ProfileWidgetCard>
      {isOpenAdd && (
        <LinkedProfileModal
          isActive={isOpenAdd}
          onClose={() => setIsOpenAdd(false)}
          contactId={contact?.contactId || ""}
          defaultValues={
            currentLinkedProfile
              ? {
                  id: currentLinkedProfile?.id || "",
                  customer: {
                    value: currentLinkedProfile?.linkedCustomerId,
                    label: currentLinkedProfile?.linkedCustomer?.name || "",
                    avatar: getInitialsFromFullName(
                      currentLinkedProfile?.linkedCustomer?.name || "",
                    ),
                    avatarColor: currentLinkedProfile?.linkedCustomer
                      ?.businessContactId
                      ? "purple"
                      : "blue",
                  },
                  relationship: currentLinkedProfile?.relation || "",
                }
              : undefined
          }
        />
      )}
      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
    </>
  );
};
